import { FC } from 'react';
import { useIntl } from 'react-intl';
import { createBemBlockBuilder, Hyperlink } from '@ebsco-ui/ebsco-ui';

import {
  FeatureFlags,
  getErrorNotificationProps,
  HoldsAndCheckoutsMessage,
  ModalNotification,
  ModalNotificationType,
} from '@app/components';
import { useAppContext } from '@app/contexts';
import { useHoldsAndCheckoutsPageConfig } from '@app/hooks';
import { FEATURE, NOTIFICATION_STATUS } from '@app/constants';

import './PlaceHoldNotification.scss';

interface PlaceHoldNotificationProps {
  type: ModalNotificationType;
  queuePosition?: number;
  errorMessage?: string;
}

const cnBem = createBemBlockBuilder(['holdModalNotification']);

export const PlaceHoldNotification: FC<PlaceHoldNotificationProps> = ({
  type,
  queuePosition = 1,
  errorMessage = '',
}) => {
  const appContext = useAppContext();
  const { $t } = useIntl();
  const { holdsAndCheckoutsRoute, selectConfiguredOption } =
    useHoldsAndCheckoutsPageConfig();

  const getMessageLink = chunks => (
    <Hyperlink href={holdsAndCheckoutsRoute} target="_self" styleType="title">
      {chunks}
    </Hyperlink>
  );

  const modalNotifications = {
    success: {
      type: NOTIFICATION_STATUS.success,
      header: selectConfiguredOption(
        $t({
          id: 'placeHoldModal.itemWasRequested',
          defaultMessage: 'Item was requested',
        }),
        $t({
          id: 'placeHoldModal.holdHasBeenPlaced',
          defaultMessage: 'Hold has been placed',
        })
      ),
      children: (
        <FeatureFlags
          flagsToMatch={[{ name: FEATURE.consortiaLoginFlow }]}
          renderOff={() =>
            $t(
              {
                id: 'placeHoldModal.youAreCurrentlyInLine',
                defaultMessage:
                  'You are currently {numberInLine, selectordinal, one {#st} two {#nd} few {#rd} other {#th}} in line.',
              },
              { numberInLine: queuePosition }
            )
          }
        />
      ),
    },
    information: {
      type: NOTIFICATION_STATUS.information,
      header: (
        <HoldsAndCheckoutsMessage
          requestsMessage={$t({
            id: 'placeHoldModal.requested',
            defaultMessage: 'Requested',
          })}
          holdsMessage={$t({
            id: 'placeHoldModal.onHold',
            defaultMessage: 'On hold',
          })}
        />
      ),
      children: (
        <HoldsAndCheckoutsMessage
          requestsMessage={$t(
            {
              id: 'placeHoldModal.alreadyRequested',
              defaultMessage:
                'You already requested this item. Manage your request in your <a>requests & loans</a>.',
            },
            { a: getMessageLink }
          )}
          holdsMessage={$t(
            {
              id: 'placeHoldModal.alreadyOnHold',
              defaultMessage:
                'You already have this item on hold. Manage your hold in your <a>holds & checkouts</a>.',
            },
            { a: getMessageLink }
          )}
        />
      ),
    },
    error: getErrorNotificationProps({
      title: (
        <HoldsAndCheckoutsMessage
          requestsMessage={$t({
            id: 'placeHoldModal.unableToRequestItem',
            defaultMessage: 'We were unable to request this book.',
          })}
          holdsMessage={$t({
            id: 'placeHoldModal.unableToPlaceHold',
            defaultMessage: 'We were unable to place a hold on this book.',
          })}
        />
      ),
      message: errorMessage,
    }),
  };

  return (
    <>
      <ModalNotification type={type} modalNotifications={modalNotifications} />
      {type === 'success' && (
        <FeatureFlags
          flagsToMatch={[{ name: FEATURE.consortiaLoginFlow }]}
          renderOff={() => (
            <p className={cnBem('__notificationText')}>
              {$t(
                {
                  id: 'placeHoldModal.wellLetYouKnow',
                  defaultMessage:
                    'When the item is available we’ll let you know at this email address: {email}',
                },
                {
                  email: (
                    <Hyperlink
                      href={`mailto:${appContext.patronSession?.user?.email}`}
                      target="_self"
                      styleType="title"
                    >
                      {appContext.patronSession?.user?.email}
                    </Hyperlink>
                  ),
                }
              )}
            </p>
          )}
        />
      )}
    </>
  );
};
